/**
 * NodeDetailPanel — slide-out panel showing full execution detail for a graph node.
 *
 * Opened by clicking a node in the ExecutionPlayground. Shows:
 * - Task ID, agent name, status, duration
 * - Prompt sent (with upstream context injection highlighted)
 * - Output received
 * - Telemetry (tokens, cost, model, provider)
 * - LangFuse trace link (if available)
 */

import { XMarkIcon, ClockIcon, CpuChipIcon, DocumentDuplicateIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import OutputRenderer from '@/components/agents/outputs/OutputRenderer'

export interface NodeDetail {
  taskId: string
  agentName: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  prompt?: string
  output?: string
  durationMs?: number
  inputTokens?: number
  outputTokens?: number
  totalTokens?: number
  costUsd?: number
  modelUsed?: string
  providerUsed?: string
  contextInjected?: string  // upstream outputs that were prepended
  langfuseTraceId?: string
  error?: string
  description?: string
  videoPath?: string  // video_gen: filename from render output (e.g. "abc123-scenecomposer.mp4")
  argusRecordingId?: string  // argus: id of the ArgusRecording row this task produced
  requestId?: string         // execution_log.request_id — lets renderers fetch full execution detail
}

interface NodeDetailPanelProps {
  node: NodeDetail | null
  onClose: () => void
  /** 'slideout' (default, legacy) renders as a fixed-position drawer.
   *  'docked' renders inline, filling the parent container. */
  mode?: 'slideout' | 'docked'
  /** Optional callback when user clicks Edit & Re-run. The Playground
   *  catches this and opens the ForkPromptModal. */
  onEditAndRun?: (node: NodeDetail) => void
}

export default function NodeDetailPanel({ node, onClose, mode = 'slideout', onEditAndRun }: NodeDetailPanelProps) {
  if (!node) return null

  const statusColors: Record<string, string> = {
    pending: 'text-muted-foreground bg-muted',
    running: 'text-amber-600 bg-amber-500/15',
    completed: 'text-emerald-600 bg-emerald-500/15',
    failed: 'text-red-600 bg-red-500/15',
    cancelled: 'text-muted-foreground bg-muted line-through',
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    toast.success(`${label} copied`)
  }

  const rootClass = mode === 'docked'
    ? 'h-full bg-card flex flex-col overflow-hidden'
    : 'fixed inset-y-0 right-0 z-50 w-[480px] bg-card border-l border-border shadow-2xl flex flex-col overflow-hidden'

  return (
    <div className={rootClass}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-foreground truncate">
            Task: {node.taskId}
          </h3>
          <div className="flex items-center gap-2 mt-0.5">
            <CpuChipIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs text-muted-foreground">{node.agentName}</span>
            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${statusColors[node.status] || ''}`}>
              {node.status}
            </span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted transition-colors shrink-0"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Description */}
        {node.description && (
          <div>
            <p className="text-xs text-muted-foreground">{node.description}</p>
          </div>
        )}

        {/* Telemetry row */}
        <div className="grid grid-cols-3 gap-2">
          {node.durationMs != null && (
            <div className="bg-muted/50 rounded-lg p-2">
              <p className="text-[10px] text-muted-foreground">Duration</p>
              <p className="text-sm font-semibold text-foreground">
                {(node.durationMs / 1000).toFixed(1)}s
              </p>
            </div>
          )}
          {node.totalTokens != null && node.totalTokens > 0 && (
            <div className="bg-muted/50 rounded-lg p-2">
              <p className="text-[10px] text-muted-foreground">Tokens</p>
              <p className="text-sm font-semibold text-foreground">
                {node.totalTokens.toLocaleString()}
              </p>
            </div>
          )}
          {node.costUsd != null && node.costUsd > 0 && (
            <div className="bg-muted/50 rounded-lg p-2">
              <p className="text-[10px] text-muted-foreground">Cost</p>
              <p className="text-sm font-semibold text-foreground">
                ${node.costUsd.toFixed(4)}
              </p>
            </div>
          )}
        </div>

        {/* Model + provider */}
        {(node.modelUsed || node.providerUsed) && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {node.modelUsed && <span className="px-2 py-0.5 bg-muted rounded">{node.modelUsed}</span>}
            {node.providerUsed && <span className="px-2 py-0.5 bg-muted rounded">{node.providerUsed}</span>}
          </div>
        )}

        {/* Agent-specific output renderer (argus session, etc.)
         *  Skipped for video_gen because the legacy block below already renders
         *  the MP4 — one renderer at a time per node.
         */}
        {node.agentName !== 'video_gen' && node.agentName !== 'video-generation' && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
              Agent Output
            </p>
            <OutputRenderer
              agent={{ name: node.agentName }}
              execution={{
                id: node.taskId,
                request_id: node.requestId ?? null,
                agent_name: node.agentName,
                status: node.status,
                argus_recording_id: node.argusRecordingId ?? null,
              }}
              result={{ output: node.output, agent_data: {} }}
              extra={{ recording_id: node.argusRecordingId }}
            />
          </div>
        )}

        {/* Video preview — for video_gen agent runs */}
        {node.videoPath && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Video Preview</p>
            <div className="rounded-lg overflow-hidden border border-border bg-black">
              <video
                src={`/api/v1/videos/${node.videoPath}`}
                controls
                className="w-full max-h-[300px]"
                preload="metadata"
              >
                Your browser does not support video playback.
              </video>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <a
                href={`/api/v1/videos/${node.videoPath}`}
                download={node.videoPath}
                className="text-[10px] text-primary hover:underline"
              >
                Download MP4
              </a>
              <span className="text-[10px] text-muted-foreground">{node.videoPath}</span>
            </div>
          </div>
        )}

        {/* Prompt sent */}
        {node.prompt && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Prompt Sent</p>
              <div className="flex items-center gap-1">
                {onEditAndRun && (
                  <button
                    onClick={() => onEditAndRun(node)}
                    className="text-primary hover:bg-primary/10 px-2 py-0.5 rounded text-[10px] font-medium flex items-center gap-1"
                    title="Edit prompt and re-run as a fork"
                  >
                    <PencilSquareIcon className="w-3 h-3" /> Edit &amp; Re-run
                  </button>
                )}
                <button
                  onClick={() => copyToClipboard(node.prompt!, 'Prompt')}
                  className="text-muted-foreground hover:text-foreground p-0.5"
                >
                  <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <pre className="text-xs bg-muted/50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap max-h-48 overflow-y-auto font-mono border border-border">
              {node.prompt}
            </pre>
          </div>
        )}

        {/* Context injected (upstream outputs) */}
        {node.contextInjected && (
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              Context Injected (from upstream)
            </p>
            <pre className="text-xs bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap max-h-36 overflow-y-auto font-mono">
              {node.contextInjected}
            </pre>
          </div>
        )}

        {/* Output received */}
        {node.output && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Output Received</p>
              <button
                onClick={() => copyToClipboard(node.output!, 'Output')}
                className="text-muted-foreground hover:text-foreground"
              >
                <DocumentDuplicateIcon className="w-3.5 h-3.5" />
              </button>
            </div>
            <pre className="text-xs bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap max-h-64 overflow-y-auto font-mono">
              {node.output}
            </pre>
          </div>
        )}

        {/* Error */}
        {node.error && (
          <div>
            <p className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-1">Error</p>
            <pre className="text-xs bg-red-500/10 border border-red-500/20 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap max-h-36 overflow-y-auto font-mono text-red-600">
              {node.error}
            </pre>
          </div>
        )}

        {/* LangFuse link */}
        {node.langfuseTraceId && (
          <a
            href={`https://langfuse.ziro.pub/trace/${node.langfuseTraceId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs text-primary hover:underline"
          >
            View in LangFuse &rarr;
          </a>
        )}
      </div>
    </div>
  )
}
